import React, { useEffect, useRef, useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import type { VacationReceipt } from '../../models/vacationReceipt.types';
import { DeleteReceiptModal, ReceiptRow, ReceiptUploadForm } from './components';
import {
    ADD_LABEL,
    COLUMN_LABELS,
    EMPTY_MESSAGE,
    ERROR_TITLE,
    LOADING_MESSAGE,
    SECTION_DESCRIPTION,
    SECTION_TITLE,
} from './vacationReceipts.definitions';
import type { VacationReceiptsProps } from './vacationReceipts.types';

const TH = 'px-3 py-3.5 text-left text-sm font-semibold text-gray-900';

export const VacationReceipts: React.FC<VacationReceiptsProps> = ({
    receipts,
    isLoading = false,
    error = null,
    canEdit = false,
    title = SECTION_TITLE,
    description = SECTION_DESCRIPTION,
    isUploading = false,
    uploadFailed = false,
    uploadProgress = 0,
    deletingId = null,
    requestOptions = [],
    onUpload,
    onDelete,
}) => {
    const [isFormOpen, setFormOpen] = useState(false);
    const [pendingDelete, setPendingDelete] = useState<VacationReceipt | null>(null);

    const wasUploading = useRef(false);
    useEffect(() => {
        if (wasUploading.current && !isUploading && !uploadFailed) setFormOpen(false);
        wasUploading.current = isUploading;
    }, [isUploading, uploadFailed]);

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h3 className="text-lg font-medium leading-6 text-gray-900">{title}</h3>
                    <p className="mt-1 text-sm text-gray-500">{description}</p>
                </div>

                {canEdit && !isFormOpen && (
                    <button
                        type="button"
                        onClick={() => setFormOpen(true)}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                    >
                        <PlusIcon className="h-4 w-4" aria-hidden="true" />
                        {ADD_LABEL}
                    </button>
                )}
            </div>

            {error && (
                <div className="rounded-md border-l-4 border-red-400 bg-red-50 p-4" role="alert">
                    <p className="text-sm font-medium text-red-800">{ERROR_TITLE}</p>
                    <p className="mt-1 text-sm text-red-700">{error}</p>
                </div>
            )}

            {isFormOpen && (
                <ReceiptUploadForm
                    isUploading={isUploading}
                    uploadProgress={uploadProgress}
                    requestOptions={requestOptions}
                    onSubmit={onUpload}
                    onCancel={() => setFormOpen(false)}
                />
            )}

            <div className="overflow-x-auto rounded-lg shadow ring-1 ring-black ring-opacity-5">
                <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                {COLUMN_LABELS.file}
                            </th>
                            <th scope="col" className={TH}>{COLUMN_LABELS.reference}</th>
                            <th scope="col" className={TH}>{COLUMN_LABELS.source}</th>
                            <th scope="col" className={TH}>{COLUMN_LABELS.uploaded}</th>
                            <th scope="col" className="py-3.5 pl-3 pr-4 text-right text-sm font-semibold text-gray-900 sm:pr-6">
                                {COLUMN_LABELS.actions}
                            </th>
                        </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-200 bg-white">
                        {isLoading && (
                            <tr>
                                <td colSpan={5} className="py-8 text-center text-gray-500">
                                    {LOADING_MESSAGE}
                                </td>
                            </tr>
                        )}

                        {!isLoading &&
                            receipts.map((receipt) => (
                                <ReceiptRow
                                    key={receipt.id}
                                    receipt={receipt}
                                    canEdit={canEdit}
                                    isDeleting={deletingId === receipt.id}
                                    onRequestDelete={setPendingDelete}
                                />
                            ))}

                        {!isLoading && receipts.length === 0 && (
                            <tr>
                                <td colSpan={5} className="py-8 text-center text-gray-500">
                                    {EMPTY_MESSAGE}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <DeleteReceiptModal
                receipt={pendingDelete}
                onCancel={() => setPendingDelete(null)}
                onConfirm={(receipt) => {
                    onDelete(receipt);
                    setPendingDelete(null);
                }}
            />
        </div>
    );
};

export default VacationReceipts;
